import { Button, Container } from '@playcanvas/pcui';

import { Events } from '../events';
import { ShortcutManager } from '../shortcut-manager';
import { localize } from './localization';
import { Tooltips } from './tooltips';

// The tab row heading the bottom dock. At most one dock panel is open at a time, and clicking
// the open tab closes it, so the dock can collapse to this row and the status bar.
class BottomTabs extends Container {
    private editorEvents: Events;

    private tooltips: Tooltips;

    private tabs = new Map<string, Button>();

    private activeTab: string | null = null;

    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'bottom-tabs'
        };

        super(args);

        this.editorEvents = events;
        this.tooltips = tooltips;
        this.dom.setAttribute('role', 'tablist');

        events.function('bottomDock.panel', () => this.activeTab);
        events.on('bottomDock.toggle', (id: string) => this.toggle(id));

        const shortcutManager: ShortcutManager = events.invoke('shortcutManager');
        const withShortcut = (localeKey: string, shortcutId: string) => {
            const text = localize(localeKey);
            const shortcut = shortcutManager.formatShortcut(shortcutId);
            return shortcut ? `${text} ( ${shortcut} )` : text;
        };

        this.addTab('timeline', localize('status-bar.timeline'), withShortcut('tooltip.status-bar.timeline', 'timelinePanel.toggle'));
        this.addTab('splatData', localize('status-bar.splat-data'), withShortcut('tooltip.status-bar.splat-data', 'dataPanel.toggle'));

        events.on('timelinePanel.toggle', () => this.toggle('timeline'));
        events.on('dataPanel.toggle', () => this.toggle('splatData'));
    }

    // Returns the tab's button so the owning panel can decorate it (the console adds a badge).
    addTab(id: string, text: string, tooltip: string) {
        const button = new Button({
            class: 'bottom-tab',
            text
        });
        button.dom.setAttribute('role', 'tab');
        button.dom.setAttribute('aria-selected', 'false');
        button.on('click', () => this.toggle(id));

        this.tooltips.register(button, tooltip, 'top');
        this.tabs.set(id, button);
        this.append(button);

        return button;
    }

    toggle(id: string) {
        this.setActive(this.activeTab === id ? null : id);
    }

    private setActive(id: string | null) {
        this.activeTab = id;
        this.tabs.forEach((button, tabId) => {
            const selected = tabId === id;
            button.class[selected ? 'add' : 'remove']('active');
            button.dom.setAttribute('aria-selected', String(selected));
        });
        this.editorEvents.fire('bottomDock.panelChanged', id);
    }
}

export { BottomTabs };
